import React from 'react';
import { currencyToWordsParts, displayValue, formatCurrency, getAdministratorAddress, getAdministratorCnpj, getAdministratorName, getAssignorDocument, getIntermediaryName, getReceiptValue, QuotaLineList } from './documentHelpers';

const hasValue = (value) => {
    if (value === null || value === undefined) {
        return false;
    }

    const text = String(value).trim();

    return text !== '' && text !== '-';
};

const formatAddress = ({ street, number, complement, neighborhood, city, state }) => {
    const streetLine = [
        street,
        number,
        complement,
        neighborhood,
    ].filter(hasValue).map((value) => String(value).trim()).join(', ');
    const cityLine = [
        city,
        state,
    ].filter(hasValue).map((value) => String(value).trim()).join(' - ');

    return [streetLine, cityLine].filter(Boolean).join(' - ') || '-';
};

export default function AssignmentDocument({ acquisition }) {
    const receiptValue = getReceiptValue(acquisition);
    const valueText = currencyToWordsParts(receiptValue);
    const administratorAddress = getAdministratorAddress(acquisition);
    const isCnpj = acquisition.doc_type === 'CNPJ';
    const assignorDocument = getAssignorDocument(acquisition);
    const city = acquisition.registry_city || acquisition.address_city || '-';
    const signatureRecognition = acquisition.check_firma === 'S'
        ? '* Reconhecer firma do CEDENTE por autenticidade.'
        : '';

    return (
        <div className='intermediary-document-paper'>
            <p className='intermediary-document-title'>
                <b><u>CESSÃO DE CRÉDITOS DE COTA DE CONSÓRCIO CANCELADA<br /> CONSORCIADO EXCLUÍDO DO GRUPO</u></b>
            </p>

            <p>
                O presente Instrumento Particular de Cessão de Créditos de Cota de Consórcio
                Cancelada, portanto, Consorciado EXCLUÍDO do Grupo, reger-se-á mediante cláusulas
                e condições adiante articuladas:
            </p>

            <CedentQualification acquisition={acquisition} />

            <p>
                <b><u>CESSIONÁRIA</u></b><br />
                OBJETIVA - SOLUÇÕES EM CONSÓRCIO S/S LTDA., inscrita no CNPJ/MF sob o
                nº 13.282.501/0001-50, com sede na Rua Espírito Santo, nº 273 - bairro: Aclimação,
                São Paulo - SP (CEP: 01526-020), neste ato representada na forma dos seus estatutos sociais.
            </p>

            <p>
                <b><u>OBJETO DA CESSÃO</u></b><br />
                Todos os créditos relativos a(s) cota(s) de consórcio cancelada(s) abaixo identificada(s):
            </p>
            <QuotaLineList quotas={acquisition.quotas} />

            <p>
                <b><u>ADMINISTRADORA DO GRUPO DE CONSÓRCIO</u></b><br />
                {displayValue(getAdministratorName(acquisition))}, inscrita do CNPJ/MF sob o nº
                {' '}{displayValue(getAdministratorCnpj(acquisition))}, domiciliada na {displayValue(administratorAddress.street)}, {displayValue(administratorAddress.number)} - {displayValue(administratorAddress.neighborhood)} - {displayValue(administratorAddress.city)} - {displayValue(administratorAddress.state)}
                {' '}(CEP: {displayValue(administratorAddress.zipCode)}).
            </p>

            <p>
                <b>Cláusula 1ª</b> - Pelo presente Contrato, o(a) Cedente cede e transfere, em caráter
                irrevogável e irretratável, a Cessionária todos os direitos creditórios relativos aos valores
                pagos a título de parcelas do <u>FUNDO COMUM</u>, do <u>FUNDO DE RESERVA</u> e de <u>TAXA
                    DE ADMINISTRAÇÃO ANTECIPADA</u>, da(s) cota(s) de consórcio cancelada(s) objeto
                da presente cessão.
            </p>

            <p>
                <b>Parágrafo 1º</b> - O crédito do FUNDO COMUM consiste no percentual amortizado
                multiplicado pelo valor do bem objeto do contrato de adesão na data da assembleia de
                contemplação da cota cancelada ou, caso não ocorra a contemplação, no encerramento do
                grupo; incidindo juros legais e correção monetária a partir daí até o efetivo pagamento pela administradora.
            </p>

            <p>
                <b>Parágrafo 2º</b> - O crédito do FUNDO DE RESERVA, apurável somente no encerramento
                do grupo, consiste no rateio do saldo remanescente desse fundo entre todos os
                consorciados e na proporção dos valores (percentuais) que foram pagos individualmente
                durante o período de existência do grupo, ou seja, desde sua inauguração até o seu
                encerramento.
            </p>

            <p>
                <b>Parágrafo 3º</b> - O crédito referente a TAXA DE ADMINISTRAÇÃO ANTECIPADA
                decorre de prática da administradora de cobrar essa taxa para depois prestar o serviço,
                tornando-se indevida pelo cancelamento da cota na parte que foi antecipada.
            </p>

            <p>
                <b>Parágrafo 4º</b> - A Cessionária poderá, <u>em nome próprio</u>, exercer os atos conservatórios
                dos créditos cedidos, em juízo e fora dele, inclusive, se entender conveniente ou necessário,
                transferir a(s) cota(s) cancelada(s) para o seu próprio nome ou terceiros, independente da
                anuência da administradora do(s) grupo(s) e cota(s) objeto da cessão.
            </p>

            <p>
                <b>Cláusula 2ª</b> - De forma a operacionalizar a presente disposição translativa dos créditos,
                o(a) Cedente se compromete outorgar procuração pública dando plenos e irrestritos poderes para que
                a Cessionária possa ceder ou transferir a(s) cota(s) para o seu próprio nome, e, dentre
                outros atos, receber todos e quaisquer valores referentes a(s) cota(s) de consórcio
                cancelada(s) objeto da presente cessão.
            </p>

            <p>
                <b>Parágrafo único</b> - Tratando-se de procuração com cláusula "em causa própria", a
                Cessionária poderá, <u>em nome próprio</u>, exercer em juízo e fora dele todos os direitos
                creditórios objeto do mandato, INDEPENDENTE do presente instrumento de cessão
                de direitos creditórios.
            </p>

            <p>
                <b>Cláusula 3ª</b> - Em contrapartida a assinatura do presente Contrato, bem como a outorga
                da Procuração, a Cessionária compromete-se a pagar a(o) Cedente o valor de R$
                {' '}{formatCurrency(receiptValue).replace('R$', '').trim()} ({valueText.reais} reais{valueText.centavos}), em moeda corrente nacional, por meio de
                transferência bancária a ser feita na conta corrente de titularidade do(a) Cedente ou outra
                conta que ele(ela) venha a indicar para o recebimento.
            </p>

            <p>
                <b>Parágrafo único</b> - Mediante o efetivo depósito do valor avençado, o(a) Cedente
                outorga a Cessionária a mais ampla, plena, geral, irrevogável e irretratável quitação com
                relação a presente transação, para nada mais reclamar a qualquer título, de qualquer
                forma e a qualquer tempo, sob qualquer pretexto, bem como renuncia a qualquer direito
                relativo a(s) cota(s) de consórcio objeto da presente cessão.
            </p>

            <p>
                <b>Cláusula 4ª</b> - Pelo presente instrumento opera-se a sub-rogação convencional de todos
                os direitos, ações, privilégios e garantias do(a) Cedente em favor da Cessionária,
                tornando-a titular de tudo que cabia ele(a) enquanto consociado(a) excluído(a) do(s)
                grupo(s) de consórcio.
            </p>

            <p>
                <b>Cláusula 5ª</b> - A natureza jurídica consumerista do contrato de adesão celebrado entre
                o(a) Cedente e a administradora de consórcio não se altera em razão da presente cessão,
                como proclamado pelo STF - Tema nº 361 - transitado em 19/06/2020.
            </p>

            <p>
                <b>Cláusula 6ª</b> - O(A) Cedente declara e garante: (a) ser legítimo(a) detentor(a) da(s)
                cota(s) de consórcio objeto da presente cessão e dos direitos creditórios ora cedidos; (<b>b</b>)
                possui capacidade civil e todos os poderes necessários para em seu nome celebrar este
                Contrato, a Procuração e todos os documentos decorrentes da presente transação; (<b>c</b>)
                que nem a assinatura deste Contrato, da Procuração e dos demais documentos aqui
                previstos violam ou conflitam com qualquer Lei, incluindo, mas não se limitando, a
                violação a direito da administradora do grupo de consórcio ou de terceiros, ou
                representam, direta ou indiretamente, fraude à credor ou fraude à execução; (<b>d</b>) não há
                em curso qualquer bloqueio e/ou ação judicial da administradora do grupo de consórcio
                ou de terceiros que possa de qualquer forma prejudicar a presente transação.
            </p>

            <p>
                <b>Parágrafo único</b> - O(A) Cedente se responsabiliza pelas declarações acima
                mencionadas respondendo civil e criminalmente e se obriga a indenizar a Cessionária na
                hipótese de qualquer omissão ou falsidade em tais declarações.
            </p>

            <p>
                <b>Cláusula 7ª</b> - O(A) Cedente não poderá sob qualquer hipótese revogar a Procuração,
                bem como não poderá ceder os direitos creditórios relativos a esse Contrato para
                quaisquer terceiros sob pena de responder criminalmente por tal ato.
            </p>

            <p>
                <b>Cláusula 8ª</b> - Na hipótese do(a) Cedente, sob qualquer pretexto, ceder o(s) crédito(s)
                objeto da presente negociação para terceiros ou recebê-lo(s) indevidamente perante a
                administradora, ainda que por simples crédito em sua conta bancária ou em conta
                bancária sob sua indicação, fica obrigado a restituir a Cessionária o(s) valor(es) do(s)
                crédito(s) recebido(s) indevidamente, no prazo de 05 (cinco) dias contados da
                notificação pela Cessionária, sob pena da aplicação de uma multa não compensatória
                equivalente a 20% (vinte por cento) do(s) crédito(s) cedido(s) ou recebido(s)
                indevidamente, acrescida de uma correção de 2% (dois por cento) ao mês, respondendo
                o(a) Cedente civilmente por perdas e danos e criminalmente na hipótese de fraude ou
                estelionato.
            </p>

            <p>
                <b>Cláusula 9ª</b> - Este Contrato constitui título executivo extrajudicial para todos os fins,
                podendo ser aplicados os artigos 783 e seguintes do Código de Processo Civil na
                hipótese de qualquer medida judicial a ser tomada em decorrência do descumprimento
                de alguma das obrigações aqui contidas.
            </p>

            <p>
                <b>Parágrafo único</b> - As partes expressamente declaram válido o uso de assinaturas digitais
                no presente instrumento, nos termos do art. 784, § 4º, do CPC, independente da plataforma estar
                credenciada na Infraestrutura de Chaves Públicas Brasileira (ICP-Brasil).
            </p>

            <p>
                <b>Cláusula 10ª</b> - Este Contrato entra em vigor na data de sua assinatura e assim
                permanecerá até que todas as obrigações nele previstas tenham sido cumpridas.
            </p>

            <p>
                <b>Cláusula 11ª</b> - Este Contrato é celebrado em caráter irrevogável e irretratável,
                obrigando as partes, bem como seus herdeiros e sucessores a qualquer título e será
                regido e interpretado pelas leis brasileiras.
            </p>

            <p>
                <b>Cláusula 12ª</b> - As partes elegem o Foro Central da Comarca de São Paulo para dirimir
                quaisquer dúvidas oriundas do presente Contrato, abrindo mão de qualquer outro ainda
                que seja privilegiado.
            </p>

            <p>
                E, por estarem assim, justas e contratadas, as partes firmam o presente instrumento em 2
                vias de igual teor e forma, na presença de 2 testemunhas.
            </p>

            <br />
            <p>{displayValue(city)},</p>

            <p className='intermediary-document-title'>{isCnpj ? displayValue(acquisition.bussiness_name) : displayValue(getIntermediaryName(acquisition))}</p>
            <p className='intermediary-document-title'><b>Cedente</b></p>

            <p className='intermediary-document-title'>OBJETIVA - SOLUÇÕES EM CONSÓRCIO S/S LTDA.</p>
            <p className='intermediary-document-title'><b>Cessionária</b></p>

            <br /><br /><br /><br />

            <p>Testemunhas:</p>

            <p className='intermediary-document-title'>
                1. ______________________ &nbsp;&nbsp;&nbsp;&nbsp; 2. ________________________
            </p>

            <p><b>{signatureRecognition}</b></p>

            <p hidden id='documento'>{displayValue(acquisition.doc_type)} {displayValue(assignorDocument)}</p>
        </div>
    );
}


function CedentQualification({ acquisition }) {
    const isCnpj = acquisition.doc_type === 'CNPJ';
    const name = getIntermediaryName(acquisition);
    const document = getAssignorDocument(acquisition);
    const representativeAddress = formatAddress({
        street: acquisition.address_street,
        number: acquisition.address_number,
        complement: acquisition.address_complement,
        neighborhood: acquisition.address_neighborhood,
        city: acquisition.address_city,
        state: acquisition.address_state,
    });

    if (isCnpj) {
        const companyAddress = formatAddress({
            street: acquisition.cnpj_address_street,
            number: acquisition.cnpj_address_number,
            complement: acquisition.cnpj_address_complement,
            neighborhood: acquisition.cnpj_address_neighborhood,
            city: acquisition.cnpj_address_city,
            state: acquisition.cnpj_address_state,
        });

        return (
            <>
                <p>
                    <b><u>CEDENTE</u></b><br />
                    Razão social: {displayValue(acquisition.bussiness_name)}<br />
                    Endereço: {companyAddress}<br />
                    CEP: {displayValue(acquisition.cnpj_address_cep)}<br />
                    CNPJ: {displayValue(acquisition.cnpj_number)}
                </p>

                <p>
                    <b><u>REPRESENTADA POR:</u></b><br />
                    Nome: {displayValue(acquisition.name)}<br />
                    Qualificação: {displayValue(acquisition.nationality)}, {displayValue(acquisition.marital_status)} e {displayValue(acquisition.occupation)}<br />
                    Endereço: {representativeAddress}<br />
                    CEP: {displayValue(acquisition.address_cep)}<br />
                    CPF/CNPJ: {displayValue(acquisition.cpf_number)}
                </p>
            </>
        );
    }

    return (
        <p>
            <b><u>CEDENTE</u></b><br />
            Nome: {displayValue(name)}<br />
            Qualificação: {displayValue(acquisition.nationality)}, {displayValue(acquisition.marital_status)} e {displayValue(acquisition.occupation)}<br />
            Endereço: {representativeAddress}<br />
            CEP: {displayValue(acquisition.address_cep)}<br />
            CPF/CNPJ: {displayValue(document)}
        </p>
    );
}
